import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useState } from 'react';
import styled from 'styled-components/native';
import { useMe } from '@/atoms/Auth';
import { SccButton } from '@/components/atoms';
import { ScreenLayout } from '@/components/ScreenLayout';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { useFormExitConfirm } from '@/hooks/useFormExitConfirm';
import { useRegisterUserInterestedRegionsAndThemes } from '@/hooks/useUserTutorialProgress';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import FormExitConfirmBottomSheet from '@/modals/FormExitConfirmBottomSheet';
import ThemeSelectBottomSheet from '@/screens/InterestedRegionAndThemesFormScreen/components/ThemeSelectBottomSheet';
import InterestedFormField from '@/screens/InterestedRegionAndThemesFormScreen/components/InterestedFormFields';
import { arraysEqualAsSets, themesToChips, } from '@/screens/InterestedRegionAndThemesFormScreen/utils';
import ToastUtils from '@/utils/ToastUtils';
/**
 * 프로필 수정에서 관심 주제만 변경하는 화면. 기존 값(useMe().userInfo.interestedThemes)을
 * 초기값으로 채운 채로 등장한다.
 *
 * 저장 시 관심 지역은 기존 값을 그대로 유지한다 (서버 API가 둘 다 받는 구조이므로).
 */
export default function EditInterestedThemesScreen({ navigation, }) {
    var _a, _b;
    const { userInfo } = useMe();
    const interestedRegionIds = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.interestedRegionIds) !== null && _a !== void 0 ? _a : [];
    const initialThemes = (_b = userInfo === null || userInfo === void 0 ? void 0 : userInfo.interestedThemes) !== null && _b !== void 0 ? _b : [];
    const [selectedThemes, setSelectedThemes] = useState(initialThemes);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const registerMutation = useRegisterUserInterestedRegionsAndThemes();
    const themeChips = themesToChips(selectedThemes);
    const isFormDirty = !arraysEqualAsSets(selectedThemes, initialThemes);
    const formExitConfirm = useFormExitConfirm(action => {
        navigation.dispatch(action);
    }, { enabled: isFormDirty && !hasSubmitted });
    const handleSubmit = useCallback(() => {
        if (selectedThemes.length === 0) {
            ToastUtils.show('관심 주제를 1개 이상 선택해주세요.');
            return;
        }
        registerMutation.mutate({
            interestedRegionIds,
            interestedThemes: selectedThemes,
        }, {
            onSuccess: () => {
                setHasSubmitted(true);
                // formExitConfirm.bypass(): setHasSubmitted은 비동기라 같은 tick의
                // goBack 디스패치에는 반영되지 않으므로 ref 기반으로 즉시 우회한다.
                formExitConfirm.bypass();
                ToastUtils.show('저장되었습니다.');
                navigation.goBack();
            },
        });
    }, [
        selectedThemes,
        interestedRegionIds,
        registerMutation,
        formExitConfirm,
        navigation,
    ]);
    const handleSheetConfirm = useCallback((nextSelectedThemes) => {
        setSelectedThemes(nextSelectedThemes);
        setIsSheetOpen(false);
    }, []);
    const canSubmit = selectedThemes.length > 0;
    return (_jsx(ScreenLayout, { isHeaderVisible: true, safeAreaEdges: ['bottom'], children: _jsx(LogParamsProvider, { params: { displaySectionName: 'edit_interested_themes' }, children: _jsxs(Container, { children: [_jsxs(Content, { children: [_jsx(TitleSection, { children: _jsx(ScreenTitle, { children: "\uAD00\uC2EC \uC8FC\uC81C\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694" }) }), _jsx(InterestedFormField, { label: "\uAD00\uC2EC \uC8FC\uC81C", selectedChips: themeChips, placeholder: "\uAD00\uC2EC \uC788\uB294 \uC8FC\uC81C\uB97C \uC54C\uB824\uC8FC\uC138\uC694", elementName: "interested_theme_input", onPress: () => setIsSheetOpen(true) })] }), _jsx(BottomBar, { children: _jsx(SccButton, { text: "\uC644\uB8CC", elementName: "interested_themes_save_button", onPress: handleSubmit, buttonColor: "brand40", textColor: "white", fontFamily: font.pretendardSemibold, fontSize: 18, height: 56, isDisabled: !canSubmit || registerMutation.isPending, style: { borderRadius: 8 } }) }), _jsx(FormExitConfirmBottomSheet, { isVisible: formExitConfirm.isVisible, onConfirm: formExitConfirm.onConfirm, onCancel: formExitConfirm.onCancel }), _jsx(ThemeSelectBottomSheet, { isVisible: isSheetOpen, initialSelectedThemes: selectedThemes, onConfirm: handleSheetConfirm, onClose: () => setIsSheetOpen(false) })] }) }) }));
}
const Container = styled.View `
  flex: 1;
  background-color: ${color.white};
`;
const Content = styled.View `
  flex: 1;
  padding: 10px 20px 40px;
  gap: 40px;
`;
const TitleSection = styled.View `
  gap: 12px;
`;
const ScreenTitle = styled.Text `
  font-family: ${font.pretendardBold};
  font-size: 24px;
  line-height: 32px;
  color: ${color.gray90v2};
`;
const BottomBar = styled.View `
  padding: 16px 20px 24px;
  background-color: ${color.white};
`;
